import React, { useState, useEffect } from 'react';
import { useOnboarding } from '../contexts/OnboardingContext';
import { useWallet } from '../contexts/ServiceContext';
import { AdminPanelService } from '../services/AdminPanelService';
import DAOGovernanceService from '../services/DAOGovernanceService';
import ReputationService from '../services/ReputationService';
import ProofOfHumanityService from '../services/ProofOfHumanityService';
import { sanitizeInput, sanitizeErrorMessage } from '../utils/security';
import './AdminPanel.css';

const AdminPanel = ({ onClose }) => {
  const { address: walletAddress } = useWallet();
  const [adminPermissions, setAdminPermissions] = useState(null);
  const [moderationQueue, setModerationQueue] = useState([]);
  const [queueSummary, setQueueSummary] = useState({});
  const [recentActions, setRecentActions] = useState([]);
  const [systemStats, setSystemStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('QUEUE');
  const [selectedItem, setSelectedItem] = useState(null);
  const [moderationReason, setModerationReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filters, setFilters] = useState({
    priority: '',
    category: '',
    status: 'PENDING_ADMIN_REVIEW'
  });

  const adminService = new AdminPanelService();
  const daoService = new DAOGovernanceService();
  const reputationService = new ReputationService();
  const humanityService = new ProofOfHumanityService();

  // Admin tabs
  const ADMIN_TABS = {
    QUEUE: { label: 'Moderation Queue', icon: '📋' },
    ACTIONS: { label: 'Recent Actions', icon: '📊' },
    USERS: { label: 'User Management', icon: '👥' },
    SYSTEM: { label: 'System Stats', icon: '⚙️' }
  };

  // Moderation decisions
  const MODERATION_DECISIONS = {
    APPROVE: {
      label: 'Approve Content',
      description: 'Content does not violate guidelines',
      icon: '✅',
      color: '#28a745',
      requiresReason: false
    },
    REMOVE: {
      label: 'Remove Content',
      description: 'Content violates guidelines and should be removed',
      icon: '🗑️',
      color: '#dc3545',
      requiresReason: true
    },
    WARN: {
      label: 'Warn User',
      description: 'Issue warning to content author',
      icon: '⚠️',
      color: '#ffc107',
      requiresReason: true
    },
    ESCALATE: {
      label: 'Escalate to Super Admin',
      description: 'Requires higher-level review',
      icon: '⬆️',
      color: '#6f42c1',
      requiresReason: true
    }
  };

  useEffect(() => {
    if (walletAddress) {
      loadAdminData();
    }
  }, [walletAddress]);

  useEffect(() => {
    if (adminPermissions?.hasPermission) {
      loadModerationQueue();
    }
  }, [filters, adminPermissions]);

  const loadAdminData = async () => {
    setLoading(true);
    setError('');

    try {
      const dashboard = await adminService.getAdminDashboard(walletAddress);
      
      if (dashboard.success) {
        setAdminPermissions(dashboard.permissions);
        setModerationQueue(dashboard.moderationQueue);
        setQueueSummary(dashboard.queueSummary);
        setRecentActions(dashboard.recentActions);
        setSystemStats(dashboard.systemStats);
      } else {
        throw new Error(dashboard.error || 'Access denied');
      }
    } catch (error) {
      console.error('Error loading admin data:', sanitizeErrorMessage(error));
      setError(sanitizeErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const loadModerationQueue = async () => {
    try {
      const result = await adminService.getModerationQueue(filters);
      if (result.success) {
        setModerationQueue(result.queue);
        setQueueSummary(result.summary);
      }
    } catch (error) {
      console.error('Error loading moderation queue:', sanitizeErrorMessage(error));
    }
  };

  const handleModeration = async (itemId, decision) => {
    if (!selectedItem || selectedItem.id !== itemId) {
      setSelectedItem(moderationQueue.find(item => item.id === itemId));
      return;
    }

    const decisionConfig = MODERATION_DECISIONS[decision];
    if (decisionConfig.requiresReason && !moderationReason.trim()) {
      setError('Please provide a reason for this decision');
      return;
    }

    setIsProcessing(true);
    setError('');
    setSuccess('');

    try {
      const result = await adminService.moderateContent(
        itemId,
        decision,
        moderationReason,
        walletAddress
      );

      if (result.success) {
        setSuccess(`Content ${decision.toLowerCase()} successfully`);
        setSelectedItem(null);
        setModerationReason('');
        
        // Reload queue
        await loadModerationQueue();
        
        // Reload recent actions
        await loadAdminData();
      } else {
        throw new Error(result.error || 'Failed to process moderation decision');
      }
    } catch (error) {
      console.error('Error processing moderation:', sanitizeErrorMessage(error));
      setError(sanitizeErrorMessage(error));
    } finally {
      setIsProcessing(false);
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      CRITICAL: '#dc3545',
      HIGH: '#fd7e14',
      MEDIUM: '#ffc107',
      LOW: '#28a745'
    };
    return colors[priority] || '#6c757d';
  };

  if (!walletAddress) {
    return (
      <div className="admin-panel-modal">
        <div className="admin-panel-container">
          <div className="admin-header">
            <h3>Admin Panel</h3>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
          <div className="admin-error">
            <p>Please connect your wallet to access the admin panel.</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-panel-modal">
        <div className="admin-panel-container">
          <div className="admin-header">
            <h3>Admin Panel</h3>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading admin dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!adminPermissions?.hasPermission) {
    return (
      <div className="admin-panel-modal">
        <div className="admin-panel-container">
          <div className="admin-header">
            <h3>Admin Panel</h3>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
          <div className="access-denied">
            <div className="access-denied-icon">🚫</div>
            <h4>Access Denied</h4>
            <p>You don't have sufficient permissions to access the admin panel.</p>
            <div className="requirements">
              <h5>Requirements:</h5>
              <ul>
                <li>Minimum 50,000 ZNOOZINU tokens for Moderator role</li>
                <li>Minimum 500 reputation score</li>
                <li>Account in good standing</li>
              </ul>
            </div>
            {adminPermissions && (
              <div className="current-status">
                <p><strong>Your Status:</strong></p>
                <p>Tokens: {adminPermissions.current || 0} / {adminPermissions.required || 50000}</p>
                <p>Reason: {adminPermissions.reason}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel-modal">
      <div className="admin-panel-container">
        <div className="admin-header">
          <div className="admin-header-left">
            <h3>🛡️ Admin Panel</h3>
            <div className="admin-role">
              <span className="role-badge">{adminPermissions.role}</span>
            </div>
          </div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {/* Admin Stats */}
        <div className="admin-stats">
          <div className="stat-card">
            <div className="stat-value">{queueSummary.total || 0}</div>
            <div className="stat-label">Pending Items</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{queueSummary.byPriority?.CRITICAL || 0}</div>
            <div className="stat-label">Critical</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{queueSummary.avgAge || 0}h</div>
            <div className="stat-label">Avg Age</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{recentActions.length}</div>
            <div className="stat-label">Recent Actions</div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="admin-tabs">
          {Object.entries(ADMIN_TABS).map(([key, tab]) => (
            <button
              key={key}
              className={`admin-tab ${activeTab === key ? 'active' : ''}`}
              onClick={() => setActiveTab(key)}
            >
              <span className="tab-icon">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {error && (
          <div className="admin-error">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        {success && (
          <div className="admin-success">
            <span className="success-icon">✅</span>
            {success}
          </div>
        )}

        {/* Tab Content */}
        <div className="admin-content">
          {activeTab === 'QUEUE' && (
            <div className="moderation-queue">
              {/* Queue Filters */}
              <div className="queue-filters">
                <select
                  value={filters.priority}
                  onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value }))}
                >
                  <option value="">All Priorities</option>
                  <option value="CRITICAL">Critical</option>
                  <option value="HIGH">High</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="LOW">Low</option>
                </select>
                
                <select
                  value={filters.category}
                  onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                >
                  <option value="">All Categories</option>
                  <option value="SPAM">Spam</option>
                  <option value="HARASSMENT">Harassment</option>
                  <option value="HATE_SPEECH">Hate Speech</option>
                  <option value="MISINFORMATION">Misinformation</option>
                  <option value="INAPPROPRIATE">Inappropriate</option>
                </select>

                <select
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                >
                  <option value="">All Statuses</option>
                  <option value="PENDING_ADMIN_REVIEW">Pending Review</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="RESOLVED">Resolved</option>
                </select>
              </div>

              {/* Queue Items */}
              <div className="queue-items">
                {moderationQueue.length === 0 ? (
                  <div className="no-items">
                    <div className="no-items-icon">📭</div>
                    <h4>No items in queue</h4>
                    <p>All moderation items have been processed.</p>
                  </div>
                ) : (
                  moderationQueue.map(item => (
                    <div
                      key={item.id}
                      className={`queue-item ${selectedItem?.id === item.id ? 'selected' : ''}`}
                    >
                      <div className="item-header">
                        <div className="item-meta">
                          <span 
                            className="priority-badge"
                            style={{ backgroundColor: getPriorityColor(item.priority) }}
                          >
                            {item.priority}
                          </span>
                          <span className="item-source">{item.source}</span>
                          <span className="item-age">{formatTimeAgo(item.timestamp || item.createdAt)}</span>
                        </div>
                        <div className="item-category">
                          {item.flagData?.category || 'Unknown'}
                        </div>
                      </div>

                      <div className="item-content">
                        <h5>Content ID: {item.contentId}</h5>
                        <p className="flag-description">
                          {item.flagData?.description || 'No description provided'}
                        </p>
                        
                        {item.flagData?.evidence && (
                          <div className="evidence">
                            <strong>Evidence:</strong> {item.flagData.evidence}
                          </div>
                        )}

                        {item.daoResults && (
                          <div className="dao-results">
                            <strong>DAO Voting Results:</strong>
                            <div className="dao-summary">
                              Winning Option: {item.daoResults.winningOption} 
                              ({item.daoResults.totalVotes} votes, {item.daoResults.totalStake} ZNOOZINU)
                            </div>
                          </div>
                        )}
                      </div>

                      {selectedItem?.id === item.id ? (
                        <div className="moderation-actions">
                          <div className="reason-input">
                            <label>Moderation Reason:</label>
                            <textarea
                              value={moderationReason}
                              onChange={(e) => setModerationReason(sanitizeInput(e.target.value))}
                              placeholder="Provide reason for your decision..."
                              rows={3}
                            />
                          </div>

                          <div className="action-buttons">
                            {Object.entries(MODERATION_DECISIONS).map(([decision, config]) => (
                              <button
                                key={decision}
                                className={`action-btn ${decision.toLowerCase()}`}
                                onClick={() => handleModeration(item.id, decision)}
                                disabled={isProcessing}
                                style={{ borderColor: config.color }}
                              >
                                <span className="action-icon">{config.icon}</span>
                                {config.label}
                              </button>
                            ))}
                          </div>

                          <div className="cancel-actions">
                            <button
                              className="cancel-btn"
                              onClick={() => {
                                setSelectedItem(null);
                                setModerationReason('');
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="item-actions">
                          <button
                            className="review-btn"
                            onClick={() => setSelectedItem(item)}
                          >
                            Review & Moderate
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === 'ACTIONS' && (
            <div className="recent-actions">
              <h4>Recent Admin Actions</h4>
              <div className="actions-list">
                {recentActions.length === 0 ? (
                  <div className="no-actions">
                    <p>No recent actions recorded.</p>
                  </div>
                ) : (
                  recentActions.map((action, index) => (
                    <div key={index} className="action-item">
                      <div className="action-header">
                        <span className="action-type">{action.action}</span>
                        <span className="action-time">{formatTimeAgo(action.timestamp)}</span>
                      </div>
                      <div className="action-details">
                        <p><strong>Admin:</strong> {action.admin}</p>
                        {action.details?.contentId && (
                          <p><strong>Content:</strong> {action.details.contentId}</p>
                        )}
                        {action.details?.reason && (
                          <p><strong>Reason:</strong> {action.details.reason}</p>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === 'USERS' && (
            <div className="user-management">
              <h4>User Management</h4>
              <p>User management features coming soon...</p>
            </div>
          )}

          {activeTab === 'SYSTEM' && (
            <div className="system-stats">
              <h4>System Statistics</h4>
              <div className="stats-grid">
                <div className="stat-item">
                  <div className="stat-number">{systemStats.totalUsers || 0}</div>
                  <div className="stat-description">Total Users</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">{systemStats.totalPosts || 0}</div>
                  <div className="stat-description">Total Posts</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">{systemStats.totalFlags || 0}</div>
                  <div className="stat-description">Total Flags</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">{systemStats.activeProposals || 0}</div>
                  <div className="stat-description">Active Proposals</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
